import {
  Stack,
  Tooltip,
  Typography,
  IconButton,
  Box,
  Theme,
  LinearProgress,
  Divider,
  TextField,
  Select,
  Skeleton,
  CardContent,
  Card,
  Grid,
  CircularProgress,
  Popper,
  ClickAwayListener,
  Paper,
  List,
  ListItemButton,
  Button,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import strings from "global/constants/StringConstants";
import {
  capitalizeText,
  convertESTtoUserLocalDateAndTime,
  convertESTtoUserLocalTime,
  debounceEventHandler,
  formatDateToEndOfDay,
  formatDateToStartOfDay,
  handleErrorMessage,
  isTruthy,
  openSuccessNotification,
  splitByCamelCase,
} from "helpers/methods";
import { focusRing } from "theme/colors";
import {
  DateRange,
  FileField,
  RequestFile,
  RequestParticipant,
  SignRequest,
  SignRequestConfig,
} from "models/interfaces";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import moment from "moment";
import SignersAvatars from "./components/SignersAvatars";
import {
  CheckCircle,
  Schedule,
  Drafts,
  RateReview,
  Cancel,
  Block,
  HelpOutline,
} from "@mui/icons-material";
import AccessTime from "@mui/icons-material/AccessTime";
import EditNote from "@mui/icons-material/EditNote";
import TaskAlt from "@mui/icons-material/TaskAlt";
import CancelIcon from "@mui/icons-material/Cancel";
import ErrorIcon from "@mui/icons-material/Error";
import BlockIcon from "@mui/icons-material/Block";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import SearchIcon from "assets/icons/search_icon.svg";
import SearchResultRow from "global/components/SearchResultRow/SearchResultRow";
import { CustomStatusChip } from "global/components";
import { StatusChipTone } from "global/components/CustomStatusChip/CustomStatusChip";
import CustomDateRangePicker from "global/components/CustomDateRangePicker/CustomDateRangePicker";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import {
  downloadSignedPdfAsZip,
  fetchCallbackKeyForSignNow,
  getAutoDelegateConfig,
  getAutoEmailReminder,
  getAutoReminderConfig,
  getExpirationDays,
  getSignRequestData,
  resendSignRequest,
  updateAutoEmailReminder,
  updateAutoReminderConfig,
  updateDelegation,
  updateExpirationDays,
  voidSignRequest,
} from "./MySignRequestService";
import React from "react";
import { ca, th } from "date-fns/locale";
import { selectEmail } from "redux/authSlice";
import { useAppSelector } from "hooks/use-app-select";
import { Sign } from "crypto";
import { getAvatarColors } from "utils/avatarColors";
import { popoverMenuItems } from "screens/Templates/MyTemplateHelper";
import { get } from "lodash";
import { setChipLoader, setLoading, setReduxSignRequest } from "redux/appSlice";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  deleteDraft,
  fetchMergedSignedPdfAsZip,
  fetchMergedSignedPdfAsZipCallback,
} from "screens/SignRequest/SignRequestService";
import DownloadDocs from "assets/icons1/DownloadDocs.svg";
import {
  downloadCombinedFileAsZip,
  downloadFilesAsZip,
} from "screens/SignRequest/ParticipantScreen/ParticipantScreenService";
import urls from "global/constants/UrlConstants";
import { hasAccessTo, hasWorkspacePermission, isAdmin } from "layouts/Config";
import FeatureKeys from "global/constants/FeatureKeys";
import RequestActionsMenu, {
  RequestMenuItem,
} from "./components/RequestActionsMenu";

export type { RequestMenuItem };

/**
 * Whether a sent request can still be edited: nobody it is waiting on has acted
 * yet. Exported so the request detail page's kebab can gate its own "Update"
 * entry on exactly the rule the list uses, rather than restating it.
 */
export const isRequestEditable = (request: SignRequest) => {
  if (request.status === strings.pending) {
    if (request.approval?.approvalNeeded!) return false;
    return !request.signers.some(
      (signer) => signer.status === strings.signed && !signer.cc,
    );
  } else if (request.status === strings.inReview) {
    return !request.approval.reviewers.some(
      (reviewer) => reviewer.status === strings.approved,
    );
  }
};

const handleDeleteDraft = async (
  id: string,
  setLoader: (value: boolean) => void,
  fetchData: Function,
) => {
  try {
    setLoader(true);
    await deleteDraft(id);
    await fetchData();
    openSuccessNotification("Draft deleted successfully.");
  } catch (error: any) {
    setLoader(false);
    handleErrorMessage(error);
  }
};

/** `resend`/`void` handlers close their own popup + anchor; rows have neither. */
const noop = () => {};

/** Opens the copy/duplicate composer with a fresh copy of the request. */
const handleDuplicateRequest = async (
  request: SignRequest,
  dispatch: any,
  setLoader: (value: boolean) => void,
) => {
  try {
    setLoader(true);
    // The row payload is a summary — the composer needs the full envelope
    // (files, fields, signers) to copy from.
    const data = await getSignRequestData(request.id);
    copySignRequest(true, data, dispatch);
  } catch (error: any) {
    handleErrorMessage(error);
  } finally {
    setLoader(false);
  }
};

const handleDownloadRequest = async (request: SignRequest, dispatch: any) => {
  try {
    const data = await getSignRequestData(request.id);
    downloadFilesAsZip(data, (value: boolean) => {
      dispatch(setLoading(value));
    });
  } catch (error: any) {
    handleErrorMessage(error);
  }
};

export const getRequestMenuItems = (
  request: SignRequest,
  dispatch: any,
  setLoader: (value: boolean) => void,
  fetchData: Function,
  onMoveRequest?: (request: SignRequest) => void,
  onShareRequest?: (request: SignRequest) => void,
): RequestMenuItem[] => {
  const requestEditable =
    (request.status === strings.pending ||
      request.status === strings.inReview) &&
    isRequestEditable(request);

  // Account permission AND the active-workspace role permission (the latter is a no-op in personal
  // mode / for admins). Keeps the kebab actions in sync with the backend @RequiresWorkspacePermission.
  const signUpdatePermission =
    hasAccessTo(strings.resources.sign, strings.updatePermission) &&
    hasWorkspacePermission("document.void");
  const signDeletePermission = hasAccessTo(
    strings.resources.sign,
    strings.deletePermission,
  );
  const signSendPermission =
    hasAccessTo(strings.resources.sign, strings.sendPermission) &&
    hasWorkspacePermission("document.send");
  const signDownloadPermission =
    hasAccessTo(strings.resources.sign, strings.downloadPermission) &&
    hasWorkspacePermission("document.download");
  // Sharing grants read access to others — gate it like the other document actions
  // (the backend also enforces account sign:add + document.send + owner-only).
  const signSharePermission =
    hasAccessTo(strings.resources.sign, strings.addPermission) &&
    hasWorkspacePermission("document.send");

  const isDraft = request.status === strings.draft;
  const isCompleted = request.status === strings.signed;
  // A request is still "open" — and therefore voidable — until it is signed.
  const isOpenRequest =
    request.status === strings.pending ||
    request.status === strings.inReview ||
    request.status === strings.scheduled;
  const canSendReminder =
    (request.status === strings.pending ||
      request.status === strings.inReview) &&
    signSendPermission;

  return [
    {
      label: isDraft ? strings.editDraft : strings.viewRequest,
      onClick: () => {
        if (isDraft) {
          history.push({
            pathname: `${viewpaths.editDraftViewPath}`,
            state: request,
          });
          return;
        }
        history.push(`${viewpaths.viewRequestViewPath}/${request.id}`);
      },
    },
    ...(onShareRequest && !isDraft && signSharePermission
      ? [
          {
            label: "Share",
            onClick: () => onShareRequest(request),
          },
        ]
      : []),
    ...(canSendReminder
      ? [
          {
            label: strings.sendReminder,
            featureKey: FeatureKeys.REMINDER_MANUAL,
            confirm: {
              title: "Really want to resend request !",
              body: "Once you resend request, current signer will get notified about this sign request.",
              confirmText: strings.sendReminder,
            },
            onClick: async () => {
              await resendSignRequestHandle(request.id, setLoader, noop, noop);
              await fetchData();
            },
          },
        ]
      : []),
    ...(requestEditable && signUpdatePermission
      ? [
          {
            label: strings.update,
            onClick: () => {
              const normalized = {
                ...request,
                files: (request.files || []).map((f: any) => ({
                  ...f,
                  unsignedDoc: f.unsignedDoc || f.unsignedPdf || "",
                  unsignedDocUrl: f.unsignedDocUrl || f.unsignedPdfUrl || "",
                })),
              } as any;
              dispatch(setReduxSignRequest(normalized));
              history.push({
                pathname: `${viewpaths.updateSignRequestsViewPath}`,
                state: request,
              });
            },
          },
        ]
      : []),
    ...(isCompleted && signDownloadPermission
      ? [
          {
            label: strings.download,
            onClick: () => handleDownloadRequest(request, dispatch),
          },
        ]
      : []),
    ...(onMoveRequest && signUpdatePermission
      ? [
          {
            label: strings.moveToFolder,
            onClick: () => onMoveRequest(request),
          },
        ]
      : []),
    ...(!isDraft && signSendPermission
      ? [
          {
            label: strings.duplicate,
            onClick: () => handleDuplicateRequest(request, dispatch, setLoader),
          },
        ]
      : []),
    ...(isOpenRequest && signUpdatePermission
      ? [
          {
            label: strings.voidRequest,
            danger: true,
            confirm: {
              title: "Really want to void request !",
              body: "Once a request is voided, it cannot be completed and must be created a new.",
              confirmText: strings.voidBtn,
            },
            onClick: async () => {
              await voidSignRequestHandle(request.id, setLoader, noop, noop);
              await fetchData();
            },
          },
        ]
      : []),
    ...(isDraft && signDeletePermission
      ? [
          {
            label: strings.delete,
            danger: true,
            onClick: () => {
              handleDeleteDraft(request.id, setLoader, fetchData);
            },
          },
        ]
      : []),
  ];
};

export const convertToTableData = (data: any[], requestType: string) => {
  switch (requestType) {
    case strings.sentRequest:
      return requesterFormat(data);
    case strings.inboxRequest:
      return signerFormat(data);
    default:
      return requesterFormat(data);
  }
};

export const requesterFormat = (data: any[]) => {
  return [];
};

export const signerFormat = (data: any[]) => {
  return [];
};

export const tabs = () => [
  {
    label: capitalizeText(strings.inboxRequest),
    value: strings.inboxRequest,
    key: strings.inboxRequest,
  },
  {
    label: capitalizeText(strings.sentRequest),
    value: strings.sentRequest,
    key: strings.sentRequest,
  },
  {
    label: "Shared with me",
    value: strings.sharedRequest,
    key: strings.sharedRequest,
  },
  ...(isAdmin()
    ? [
        {
          label: capitalizeText(strings.configurations),
          value: strings.configurations,
          key: strings.configurations,
        },
      ]
    : []),
];

export const initialRequestsFilterState = (
  page: number,
  rowsPerPage: number,
  searchText: string,
) => {
  return {
    page: page,
    rowsPerPage,
    searchText,
  };
};

export const getStatusColor = (status: string) => {
  switch (status) {
    case "Completed":
      return {
        color: "#47AA12",
        backgroundColor: "#F7FFF2",
      };
    case "Submitted":
      return {
        color: "#FF9436",
        backgroundColor: "#FFF6E2",
      };
    case "Draft":
      return {
        color: "#475569",
        backgroundColor: "#EFEEEA",
      };
    case "Pending":
      return {
        color: "#c23118",
        backgroundColor: "#fff2f2",
      };
    case "InProgress":
      return {
        color: "#125DDB",
        backgroundColor: "#E8EFFC",
      };
    case "Rejected":
      return {
        color: "#c23118",
        backgroundColor: "#fff2f2",
      };
    case "InReview":
      return {
        color: "#00B2FF",
        backgroundColor: "#00B2FF0F",
      };
    default:
      return {
        color: "#6B91FC",
        backgroundColor: "#F3F7FF",
      };
  }
};

const getRequestDetails = (request: SignRequest, theme: Theme) => {
  return (
    <Typography
      variant="body2"
      sx={{ color: "text.disabled", fontSize: "0.85rem", mt: 0.2 }}
    >
      Created by {request.requestedBy.name} •{" "}
      {convertESTtoUserLocalDateAndTime(
        request.createdOn,
        "MMMM D, YYYY h:mm A",
        "MM/DD/YYYY HH:mm:ss",
      )}
    </Typography>
  );
};

const getStatusMeta = (status: string, theme: Theme, requestType: string) => {
  switch (status) {
    case strings.signed:
      return {
        icon: CheckCircle,
        color: "#2e7d32", // success.main
        label: "Signed",
      };
    case strings.pending:
      return {
        icon: FiberManualRecordIcon,
        color: theme.palette.warning.main,
        label: "Pending",
      };
    case strings.scheduled:
      return {
        icon: Schedule,
        color: "#0288d1", // info.main
        label: "Scheduled",
      };
    case strings.draft:
      return {
        icon: Drafts,
        color: "#757575", // grey.600
        label: "Draft",
      };
    case strings.inReview:
      return {
        icon: RateReview,
        color: "#1976d2", // primary.main
        label: "In Review",
      };
    case strings.rejected:
      return {
        icon: Cancel,
        color: "#d32f2f", // error.main
        label: "Rejected",
      };
    case strings.inReview:
      return {
        icon: Block,
        color: "#d32f2f", // error.main
        label: "Voided",
      };
    case strings.voided:
      return {
        icon: BlockIcon,
        color: "#e57373", // error.light
        label: "Expired",
      };
    case strings.expired:
      return {
        icon: ErrorIcon,
        color: "#e57373", // light red for expired
        label: "Expired",
      };
    case strings.declined:
      return {
        icon: CancelIcon,
        color: "#e57373", // light red for expired
        label: "Expired",
      };
    default:
      return {
        icon: HelpOutline,
        color: "#9e9e9e", // text.secondary or grey.500
        label: status,
      };
  }
};

/**
 * Maps a request to its status chip, written from the reader's point of view —
 * the same "Pending" request reads "Your turn to sign" for the current signer
 * and "Waiting for others" for everyone else. Shared by the inbox page cards
 * and the top-nav inbox panel so the two never drift apart.
 */
export const getRequestStatusChip = (
  request: SignRequest,
  userEmail: string,
): { label: string; tone: StatusChipTone } => {
  const labels = strings.inboxStatusLabels;
  const signerStatus = request.signers?.find(
    (signer: RequestParticipant) => signer.email === userEmail,
  )?.status;

  if (
    request.status === strings.declined ||
    request.status === strings.rejected
  ) {
    return { label: labels.declined, tone: "danger" };
  }

  if (
    request.status === strings.signed ||
    request.status === strings.completed
  ) {
    return { label: labels.signed, tone: "success" };
  }

  if (request.status === strings.pending) {
    if (
      signerStatus === strings.delegated ||
      signerStatus === strings.Delegated
    ) {
      return { label: labels.delegatedByYou, tone: "neutral" };
    }
    return request.currentSigners?.includes(userEmail)
      ? { label: labels.yourTurn, tone: "action" }
      : { label: labels.waitingForOthers, tone: "info" };
  }

  // Voided, expired, and anything else the design doesn't cover.
  return { label: splitByCamelCase(request.status), tone: "neutral" };
};

/**
 * Status chip for sent requests. The sender sees the request's own state, so
 * unlike {@link getRequestStatusChip} this is a straight status → tone map.
 * Every status renders the same dot + pill through `CustomStatusChip` — the
 * per-status icons it replaced made the column read inconsistently.
 */
export const getSentRequestStatusChip = (
  status: string,
): { label: string; tone: StatusChipTone } => {
  const label = splitByCamelCase(status);
  switch (status) {
    case strings.signed:
    case strings.completed:
      return { label, tone: "success" };
    case strings.pending:
      return { label, tone: "action" };
    case strings.scheduled:
    case strings.inReview:
      return { label, tone: "info" };
    case strings.rejected:
    case strings.declined:
      return { label, tone: "danger" };
    // Draft, voided, expired and anything unmapped read as a quiet grey state.
    default:
      return { label, tone: "neutral" };
  }
};

// Width of the status column's contents — the chip labels ("In Review",
// "Scheduled") and the signer progress bar share it so they stay aligned.
const statusColumnWidth = 215;

// Inbox rows reserve this much for the row action. "Download" is the widest
// button that lands here, and rows with no action still hold the slot so every
// status chip ends on the same vertical line.
const inboxActionSlotWidth = 120;

const getQuickStats = (
  request: SignRequest,
  theme: Theme,
  requestType: string,
) => {
  if (requestType === strings.inboxRequest) return null;

  const { color } = getStatusMeta(request.status, theme, requestType);
  const { progress, completedCount, totalCount } = getProgress(request.signers);
  const barGradient =
    request.status === strings.signed ? color : theme.palette.brandGradient;
  const status = getSentRequestStatusChip(request.status);

  return (
    <>
      <Stack
        direction="column"
        alignItems={"flex-start"}
        spacing={0.75}
        flex={1}
        width={{ xs: "100%", md: "auto" }}
        mt={{ xs: 0, md: 0 }}
        justifyContent={"start"}
      >
        <CustomStatusChip label={status.label} tone={status.tone} />
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          width={statusColumnWidth}
          maxWidth="100%"
        >
          <Box flex={1}>
            <Tooltip
              title={`${progress.toFixed(0)}% completed`}
              placement="bottom"
              arrow
            >
              <LinearProgress
                variant="determinate"
                value={progress}
                aria-label={`Signing progress: ${completedCount} of ${totalCount} signers completed`}
                sx={{
                  height: 5,
                  borderRadius: 5,
                  backgroundColor: "#e0e0e0",
                  "& .MuiLinearProgress-bar": {
                    background: progress > 0 ? barGradient : "transparent",
                  },
                }}
              />
            </Tooltip>
          </Box>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "10px", fontWeight: 600 }}
          >
            {completedCount}/{totalCount}
          </Typography>
        </Stack>
      </Stack>
    </>
  );
};

export const handleSignNow = async (requestToken: string) => {
  // Opened synchronously (before the await below) so browser popup blockers still attribute it
  // to this click; the tab is redirected once the callback key comes back.
  const signWindow = window.open("", "_blank");
  try {
    const key = await fetchCallbackKeyForSignNow(requestToken);
    const url = `${urls.doSign}?id=${requestToken}&key=${encodeURIComponent(key)}&signer=true`;
    if (signWindow) {
      signWindow.location.href = url;
    } else {
      window.open(url, "_blank");
    }
  } catch (error: any) {
    signWindow?.close();
    handleErrorMessage(error);
  }
};

const getActions = (
  signRequest: SignRequest,
  userEmail: string,
  dispatch: any,
  isLoading: boolean,
) => {
  const isPending = signRequest.status === "Pending";
  const isCurrentSigner =
    signRequest.currentSigners.includes(userEmail) && isPending;
  const isSigned = signRequest.status === "Signed";

  if (isCurrentSigner) {
    return (
      <Stack direction="column" spacing={-1}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleSignNow(signRequest.requestToken)}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            fontWeight: 600,
            boxShadow: "none",
            px: 2,
            py: 0.5,
          }}
        >
          Sign
        </Button>
      </Stack>
    );
  }

  if (isSigned) {
    return (
      <Stack direction="column" spacing={0}>
        {hasAccessTo(strings.resources.sign, strings.downloadPermission) && (
          <Button
            variant="outlined"
            onClick={async () => {
              try {
                const data = await getSignRequestData(signRequest.id);
                downloadFilesAsZip(data, (value: boolean) => {
                  dispatch(setLoading(value));
                });
              } catch (error: any) {
                handleErrorMessage(error);
              }
            }}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 600,
              borderColor: "divider",
              color: "text.primary",
              px: 2,
              py: 0.5,
            }}
          >
            Download
          </Button>
        )}
      </Stack>
    );
  }

  // if (isPending) {
  //   return (
  //     <Box>
  //       <IconButton
  //       // onClick={() => handleSignNow(signRequest.requestToken)}
  //       >
  //         <TaskAlt />
  //       </IconButton>
  //       {/* <img src={WaitingClockIcon} alt="Waiting Icon" width="20px" /> */}
  //     </Box>
  //   );
  // }

  return null;
};

export const getRequestMenu = (
  popover: {
    open: boolean;
    anchorEl: HTMLElement | null;
    setAnchorEl: (value: React.SetStateAction<HTMLElement | null>) => void;
    onOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onClose: () => void;
  },
  requestMenuItems: RequestMenuItem[],
) => <RequestActionsMenu popover={popover} items={requestMenuItems} />;

export const getMobileVersionCard = (
  request: SignRequest,
  theme: Theme,
  requestType: string,
  userEmail: string,
  loader: boolean,
  setLoader: (value: boolean) => void,
  fetchData: Function,
  isShowRequestMenu: boolean,
  popover: {
    open: boolean;
    anchorEl: HTMLElement | null;
    setAnchorEl: (value: React.SetStateAction<HTMLElement | null>) => void;
    onOpen: () => void;
    onClose: () => void;
  },
  setRequestMenuItems: (value: RequestMenuItem[]) => void,
  dispatch: any,
  isLoading: boolean,
  onMoveRequest?: (request: SignRequest) => void,
) => {
  const isDraft = request.status === strings.draft;

  return (
    <>
      <Stack direction="column" spacing={2}>
        <Stack spacing={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="row" alignItems="center" flex={1}>
              <Tooltip title={request.emailSubject} arrow>
                <Typography
                  gutterBottom
                  variant="h6"
                  overflow="hidden"
                  whiteSpace="normal"
                  maxWidth="300px"
                  display="block"
                  sx={{
                    cursor: hasAccessTo(
                      strings.resources.sign,
                      strings.fetchPermission,
                    )
                      ? "pointer"
                      : "default",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    "&:focus": { outline: "none" },
                    "&:focus-visible": {
                      outline: `2px solid ${focusRing}`,
                      outlineOffset: "2px",
                    },
                  }}
                  onClick={() => {
                    if (isDraft) {
                      history.push({
                        pathname: `${viewpaths.editDraftViewPath}`,
                        state: request,
                      });
                      return;
                    }
                    requestType === strings.sentRequest &&
                      hasAccessTo(
                        strings.resources.sign,
                        strings.fetchPermission,
                      ) &&
                      history.push(
                        `${viewpaths.viewRequestViewPath}/${request.id}`,
                      );
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      if (isDraft) {
                        history.push({
                          pathname: `${viewpaths.editDraftViewPath}`,
                          state: request,
                        });
                        return;
                      }
                      requestType === strings.sentRequest &&
                        history.push(
                          `${viewpaths.viewRequestViewPath}/${request.id}`,
                        );
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`View request: ${request.emailSubject}`}
                >
                  {request.emailSubject}
                </Typography>
              </Tooltip>
            </Stack>
            {isShowRequestMenu &&
              (requestType === strings.sentRequest ? (
                <IconButton
                  size="small"
                  aria-label={`Actions for request ${request.emailSubject}`}
                  aria-haspopup="menu"
                  sx={{
                    color: theme.palette.text.secondary,
                    padding: 0,
                  }}
                  onClick={(e: any) => {
                    e.stopPropagation();
                    setRequestMenuItems(
                      getRequestMenuItems(
                        request,
                        dispatch,
                        setLoader,
                        fetchData,
                        onMoveRequest,
                      ),
                    );
                    popover.setAnchorEl(e.currentTarget);
                    popover.onOpen();
                  }}
                  ref={popover.anchorEl as React.Ref<HTMLButtonElement>}
                >
                  <MoreVertIcon />
                </IconButton>
              ) : (
                <>{getActions(request, userEmail, dispatch, isLoading)}</>
              ))}
          </Stack>

          {getRequestDetails(request, theme)}
        </Stack>
        {requestType === strings.sentRequest ? (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Box
              flex={0.5}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"start"}
            >
              <SignersAvatars requestParticipants={request.signers} />
            </Box>
            <Box
              flex={0.8}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"flex-end"}
            >
              {getQuickStats(request, theme, requestType)}
            </Box>
          </Stack>
        ) : (
          <Stack direction={"row"}>
            <Box
              flex={0.5}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"start"}
            >
              {(() => {
                const status = getRequestStatusChip(request, userEmail);
                return (
                  <CustomStatusChip label={status.label} tone={status.tone} />
                );
              })()}
            </Box>
            <Box
              flex={0.5}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              {getQuickStats(request, theme, requestType)}
            </Box>
          </Stack>
        )}
      </Stack>
    </>
  );
};

export const getDesktopVersionCard = (
  request: SignRequest,
  theme: Theme,
  requestType: string,
  userEmail: string,
  loader: boolean,
  setLoader: (value: boolean) => void,
  fetchData: Function,
  isShowRequestMenu: boolean,
  popover: {
    open: boolean;
    anchorEl: HTMLElement | null;
    setAnchorEl: (value: React.SetStateAction<HTMLElement | null>) => void;
    onOpen: () => void;
    onClose: () => void;
  },
  setRequestMenuItems: (value: RequestMenuItem[]) => void,
  dispatch: any,
  isLoading: boolean,
  onMoveRequest?: (request: SignRequest) => void,
) => {
  const isDraft = request.status === strings.draft;
  const { color: statusColor } = getStatusMeta(
    request.status,
    theme,
    requestType,
  );

  return (
    <>
      <Stack direction="row" alignItems="center" sx={{ cursor: "default" }}>
        <Stack
          flex={1.5}
          direction="row"
          alignItems="center"
          spacing={2}
          minWidth={0}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              backgroundColor: alpha(statusColor, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <DescriptionOutlinedIcon sx={{ color: statusColor }} />
          </Box>
          <Box textAlign="start" minWidth={0}>
            <Tooltip title={request.emailSubject} arrow>
              <Typography
                gutterBottom
                variant="subtitle1"
                overflow="hidden"
                whiteSpace="normal"
                maxWidth="600px"
                display="block"
                sx={{
                  cursor:
                    requestType === strings.inboxRequest ||
                    !hasAccessTo(
                      strings.resources.sign,
                      strings.fetchPermission,
                    )
                      ? "default"
                      : "pointer",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  fontWeight: 600,
                  color: "mobile.base.900",
                  "&:focus": { outline: "none" },
                  "&:focus-visible": {
                    outline: `2px solid ${focusRing}`,
                    outlineOffset: "2px",
                  },
                }}
                onClick={() => {
                  if (isDraft) {
                    history.push({
                      pathname: `${viewpaths.editDraftViewPath}`,
                      state: request,
                    });
                    return;
                  }
                  requestType === strings.sentRequest &&
                    hasAccessTo(
                      strings.resources.sign,
                      strings.fetchPermission,
                    ) &&
                    history.push(
                      `${viewpaths.viewRequestViewPath}/${request.id}`,
                    );
                }}
                onKeyPress={(e) => {
                  if (requestType === strings.inboxRequest) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (isDraft) {
                      history.push({
                        pathname: `${viewpaths.editDraftViewPath}`,
                        state: request,
                      });
                      return;
                    }
                    requestType === strings.sentRequest &&
                      history.push(
                        `${viewpaths.viewRequestViewPath}/${request.id}`,
                      );
                  }
                }}
                role={
                  requestType === strings.inboxRequest ? undefined : "button"
                }
                tabIndex={requestType === strings.inboxRequest ? undefined : 0}
                aria-label={
                  requestType === strings.inboxRequest
                    ? undefined
                    : `View request: ${request.emailSubject}`
                }
              >
                {request.emailSubject}
              </Typography>
            </Tooltip>
            {getRequestDetails(request, theme)}
          </Box>
        </Stack>
        {requestType === strings.sentRequest ? (
          <Stack
            direction="column"
            flex={0.75}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <Typography
              variant="body2"
              sx={{
                color: "#646464",
                ml: 1,
                display: { xs: "none", md: "block" },
              }}
            >
              Signers
            </Typography>
            <SignersAvatars requestParticipants={request.signers} />
          </Stack>
        ) : (
          <Box
            flex={0.75}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"flex-end"}
            pr={1.5}
          >
            {(() => {
              const status = getRequestStatusChip(request, userEmail);
              return (
                <CustomStatusChip label={status.label} tone={status.tone} />
              );
            })()}
          </Box>
        )}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          // Sent rows keep a fixed, growing status column so the progress bars
          // line up down the list. Inbox rows have no progress bar, so they
          // reserve a fixed action slot instead: the button varies ("Sign",
          // "Download", or nothing at all) and without a reserved slot each row
          // would push its chip to a different spot.
          flex={requestType === strings.sentRequest ? 0.9 : "0 0 auto"}
          minWidth={
            requestType === strings.sentRequest
              ? statusColumnWidth
              : inboxActionSlotWidth
          }
          width={
            requestType === strings.sentRequest
              ? undefined
              : inboxActionSlotWidth
          }
          // Matches the folder row's inset: 12px here plus the icon button's own
          // 8px keeps both lists' more-icons on the same vertical line.
          pr={requestType === strings.sentRequest ? 1.5 : 0}
          justifyContent="flex-end"
        >
          {getQuickStats(request, theme, requestType)}
          {isShowRequestMenu &&
            (requestType === strings.sentRequest ? (
              // Default padding (no `size="small"`, no `padding: 0`) so the
              // hover halo matches the folder row's more-icon.
              <IconButton
                aria-label={`Actions for request ${request.emailSubject}`}
                aria-haspopup="menu"
                sx={{ color: theme.palette.text.secondary }}
                onClick={(e: any) => {
                  e.stopPropagation();
                  setRequestMenuItems(
                    getRequestMenuItems(
                      request,
                      dispatch,
                      setLoader,
                      fetchData,
                      onMoveRequest,
                    ),
                  );
                  popover.setAnchorEl(e.currentTarget);
                  popover.onOpen();
                }}
                ref={popover.anchorEl as React.Ref<HTMLButtonElement>}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            ) : (
              <>{getActions(request, userEmail, dispatch, isLoading)}</>
            ))}
        </Stack>
      </Stack>
    </>
  );
};

export const getDashboardRecentActivityCard = (
  request: SignRequest,
  theme: Theme,
  requestType: string,
) => {
  const isDraft = request.status === strings.draft;
  const { icon: Icon, color } = getStatusMeta(
    request.status,
    theme,
    requestType,
  );
  const { progress, completedCount, totalCount } = getProgress(request.signers);
  const dotColor = request.status === strings.pending ? "primary.main" : color;
  const barGradient =
    request.status === strings.signed ? color : theme.palette.brandGradient;

  return (
    <Stack direction="column" spacing={0.5} sx={{ cursor: "default" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
      >
        <Tooltip title={request.emailSubject} arrow>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{
              cursor:
                requestType === strings.inboxRequest ||
                !hasAccessTo(strings.resources.sign, strings.fetchPermission)
                  ? "default"
                  : "pointer",
              maxWidth: "70%",
              flex: "0 1 70%",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
              lineHeight: 1.3,
              "&:focus": { outline: "none" },
              "&:focus-visible": {
                outline: `2px solid ${focusRing}`,
                outlineOffset: "2px",
              },
            }}
            onClick={() => {
              if (isDraft) {
                history.push({
                  pathname: `${viewpaths.editDraftViewPath}`,
                  state: request,
                });
                return;
              }
              requestType === strings.sentRequest &&
                hasAccessTo(strings.resources.sign, strings.fetchPermission) &&
                history.push(`${viewpaths.viewRequestViewPath}/${request.id}`);
            }}
            onKeyPress={(e) => {
              if (requestType === strings.inboxRequest) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                if (isDraft) {
                  history.push({
                    pathname: `${viewpaths.editDraftViewPath}`,
                    state: request,
                  });
                  return;
                }
                requestType === strings.sentRequest &&
                  hasAccessTo(
                    strings.resources.sign,
                    strings.fetchPermission,
                  ) &&
                  history.push(
                    `${viewpaths.viewRequestViewPath}/${request.id}`,
                  );
              }
            }}
            role={requestType === strings.inboxRequest ? undefined : "button"}
            tabIndex={requestType === strings.inboxRequest ? undefined : 0}
            aria-label={
              requestType === strings.inboxRequest
                ? undefined
                : `View request: ${request.emailSubject}`
            }
          >
            {request.emailSubject}
          </Typography>
        </Tooltip>
        <Box sx={{ flexShrink: 0 }}>
          <SignersAvatars requestParticipants={request.signers} />
        </Box>
      </Stack>

      <Typography variant="caption" sx={{ color: "#646464" }}>
        Created by {request.requestedBy.name} •{" "}
        {convertESTtoUserLocalDateAndTime(
          request.createdOn,
          "MMMM D, YYYY h:mm A",
          "MM/DD/YYYY HH:mm:ss",
        )}
      </Typography>

      <Stack spacing={0.6}>
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{
            backgroundColor: alpha(color, 0.1),
            padding: "2px 10px",
            borderRadius: "16px",
            width: "fit-content",
          }}
        >
          <Icon sx={{ fontSize: "13px", color: dotColor }} />
          <Typography
            variant="caption"
            fontSize="12px"
            fontWeight={600}
            color={color}
          >
            {splitByCamelCase(request.status)}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          width={160}
          maxWidth="100%"
        >
          <Box flex={1}>
            <Tooltip
              title={`${progress.toFixed(0)}% completed`}
              placement="bottom"
              arrow
            >
              <LinearProgress
                variant="determinate"
                value={progress}
                aria-label={`Signing progress: ${completedCount} of ${totalCount} signers completed`}
                sx={{
                  height: 5,
                  borderRadius: 5,
                  backgroundColor: "#e0e0e0",
                  "& .MuiLinearProgress-bar": {
                    background: progress > 0 ? barGradient : "transparent",
                  },
                }}
              />
            </Tooltip>
          </Box>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "10px", fontWeight: 600 }}
          >
            {completedCount}/{totalCount}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

export const getProgress = (signers: RequestParticipant[]) => {
  const completedCount = signers.filter(
    (signer) => signer.status === strings.signed && !signer.cc,
  ).length;
  const totalCount = signers.filter(
    (signer) => signer.status !== strings.Delegated && !signer.cc,
  ).length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  return { progress, completedCount, totalCount };
};

export const inboxRequestStatusOptions = [
  {
    label: "All",
    value: "All",
  },
  {
    label: strings.pending,
    value: strings.pending,
  },
  {
    label: strings.signed,
    value: strings.signed,
  },
  {
    label: strings.voided,
    value: strings.voided,
  },
  {
    label: strings.expired,
    value: strings.expired,
  },
  {
    label: strings.declined,
    value: strings.declined,
  },
];

export const RequestStatusOptions = [
  {
    label: "All",
    value: "All",
  },
  {
    label: strings.pending,
    value: strings.pending,
  },
  {
    label: strings.signed,
    value: strings.signed,
  },
  {
    label: strings.scheduled,
    value: strings.scheduled,
  },
  {
    label: strings.voided,
    value: strings.voided,
  },
  {
    label: strings.expired,
    value: strings.expired,
  },
  {
    label: strings.rejected,
    value: strings.rejected,
  },
  {
    label: strings.draft,
    value: strings.draft,
  },
  {
    label: strings.inReviewLabel,
    value: strings.inReview,
  },
  {
    label: strings.declined,
    value: strings.declined,
  },
];

const getDatePicker = (
  openDateRangePicker: boolean,
  setOpenDateRangePicker: (value: boolean) => void,
  dateRange: DateRange,
  setDateRange: (dateRange: DateRange) => void,
  fetchData: Function,
  loader: boolean,
) => {
  return (
    <Box
      sx={{
        backgroundColor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        "&:hover": {
          borderColor: "text.disabled",
        },
        "& .MuiTextField-root": {
          minWidth: { xs: "100% !important", md: "380px !important" },
        },
      }}
    >
      <CustomDateRangePicker
        dateRange={dateRange}
        openDateRangePicker={openDateRangePicker}
        setOpenDateRangePicker={setOpenDateRangePicker}
        setDateRange={setDateRange}
        helperFunction={fetchData}
        disabled={loader}
      />
    </Box>
  );
};

export const getFilters = (
  requestsFilters: any,
  setRequestsFilters: (filters: any) => void,
  openDateRangePicker: boolean,
  setOpenDateRangePicker: (open: boolean) => void,
  handleSearchOnChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  handleStatusChange: (event: React.ChangeEvent<{ value: unknown }>) => void,
  dateRange: DateRange,
  setDateRange: (dateRange: DateRange) => void,
  fetchData: Function,
  classes: any,
  loader: boolean,
  requestType: string,
  searchDropdownOptions?: {
    searchValue?: string;
    searchAnchorRef?: React.MutableRefObject<HTMLDivElement | null>;
    searchOpen?: boolean;
    setSearchOpen?: (v: boolean) => void;
    isSearching?: boolean;
    searchResults?: any[];
    resolveAncestorCrumbs?: (item: any) => string[];
    handleSearchSelect?: (item: any) => void;
  },
) => {
  const isControlled = searchDropdownOptions?.searchValue !== undefined;
  const dropdownEnabled =
    !!searchDropdownOptions?.handleSearchSelect &&
    !!searchDropdownOptions?.setSearchOpen;
  return (
    <Box sx={{ width: "100%" }}>
      <Stack spacing={2} sx={{ width: "100%", pb: 1 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="flex-start"
          sx={{ width: "100%" }}
        >
          <Box
            ref={searchDropdownOptions?.searchAnchorRef}
            sx={{ position: "relative", flexGrow: 1, width: "100%" }}
          >
            <TextField
              id="requests_search_box"
              type="search"
              placeholder="Search requests by name or recipient"
              inputProps={{
                "aria-label": "Search requests by name or recipient",
              }}
              variant="outlined"
              sx={{
                backgroundColor: "background.paper",
                borderRadius: 2,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "divider",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "text.disabled",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "text.disabled",
                },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
              fullWidth
              {...(isControlled
                ? {
                    value: searchDropdownOptions!.searchValue,
                    onChange: handleSearchOnChange,
                  }
                : {
                    onChange: debounceEventHandler(
                      handleSearchOnChange,
                      strings.SEARCH_TIME_OUT,
                    ),
                  })}
              InputProps={{
                startAdornment: (
                  <Stack mr={1}>
                    <img
                      src={SearchIcon}
                      width="30px"
                      height="20px"
                      alt="Search icon"
                      aria-hidden="true"
                    />
                  </Stack>
                ),
                endAdornment: searchDropdownOptions?.isSearching ? (
                  <CircularProgress
                    size={16}
                    sx={{ mr: 1 }}
                    role="status"
                    aria-label="Searching requests"
                  />
                ) : null,
              }}
              disabled={loader}
            />
            {dropdownEnabled && (
              <Popper
                open={
                  !!searchDropdownOptions!.searchOpen &&
                  !!searchDropdownOptions!.searchAnchorRef?.current
                }
                anchorEl={searchDropdownOptions!.searchAnchorRef?.current}
                placement="bottom-start"
                style={{
                  zIndex: 1300,
                  width:
                    searchDropdownOptions!.searchAnchorRef?.current
                      ?.clientWidth,
                }}
              >
                <ClickAwayListener
                  onClickAway={() =>
                    searchDropdownOptions!.setSearchOpen!(false)
                  }
                >
                  <Paper
                    elevation={4}
                    sx={{
                      mt: 0.5,
                      maxHeight: 360,
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}
                  >
                    {(searchDropdownOptions!.searchResults ?? []).length ===
                    0 ? (
                      <Box sx={{ p: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          No matches
                        </Typography>
                      </Box>
                    ) : (
                      <List dense disablePadding>
                        {(searchDropdownOptions!.searchResults ?? []).map(
                          (item: any) => {
                            const isFolder = item.type === strings.folder;
                            const title = isFolder
                              ? (item.templateName ?? "")
                              : (item.emailSubject ?? "");
                            const crumbs =
                              searchDropdownOptions!.resolveAncestorCrumbs?.(
                                item,
                              ) ?? [];
                            return (
                              <SearchResultRow
                                key={
                                  isFolder
                                    ? `f-${item.autoGeneratedId}`
                                    : `r-${item.id ?? item.autoGeneratedId}`
                                }
                                isFolder={isFolder}
                                title={title}
                                crumbs={crumbs}
                                createdBy={item.requestedBy?.name}
                                createdOn={item.createdOn ?? item.createdAt}
                                onClick={() =>
                                  searchDropdownOptions!.handleSearchSelect!(
                                    item,
                                  )
                                }
                              />
                            );
                          },
                        )}
                      </List>
                    )}
                  </Paper>
                </ClickAwayListener>
              </Popper>
            )}
          </Box>
          <Box
            sx={{ width: { xs: "100%", md: "auto" }, minWidth: { md: 380 } }}
          >
            {getDatePicker(
              openDateRangePicker,
              setOpenDateRangePicker,
              dateRange,
              setDateRange,
              fetchData,
              loader,
            )}
          </Box>
        </Stack>
        <Box sx={{ width: "100%" }}>
          {statusFilter(
            requestsFilters,
            requestType,
            handleStatusChange,
            classes,
            loader,
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export const statusFilter = (
  requestsFilters: any,
  requestType: string,
  handleStatusChange: (event: any) => void,
  classes: any,
  loader: boolean,
) => {
  const options =
    requestType === strings.sentRequest
      ? RequestStatusOptions
      : inboxRequestStatusOptions;

  return (
    <Stack
      direction="row"
      spacing={1}
      role="group"
      aria-label="Filter requests by status"
      sx={{
        overflowX: "auto",
        pb: 0.5,
        "&::-webkit-scrollbar": { height: "0px" },
      }}
    >
      {options.map((data, index: number) => {
        const isSelected = requestsFilters.status === data.value;

        return (
          <Button
            key={index}
            variant="outlined"
            aria-pressed={isSelected}
            onClick={() =>
              handleStatusChange({ target: { value: data.value } })
            }
            disabled={loader}
            sx={{
              borderRadius: "50px",
              textTransform: "none",
              minWidth: "max-content",
              height: "32px",
              px: 2.5,
              py: 0,
              fontWeight: 600,
              fontSize: "0.875rem",
              borderColor: isSelected ? "transparent" : "divider",
              backgroundColor: isSelected
                ? "mobile.base.500"
                : "background.paper",
              color: isSelected ? "common.white" : "text.primary",
              "&:hover": {
                backgroundColor: isSelected
                  ? "mobile.base.500"
                  : "action.hover",
                borderColor: isSelected ? "transparent" : "text.disabled",
              },
              "&.Mui-disabled": {
                backgroundColor: isSelected
                  ? "mobile.base.500"
                  : "background.paper",
                color: isSelected ? "common.white" : "text.disabled",
                borderColor: isSelected ? "transparent" : "divider",
              },
            }}
          >
            {data.label}
          </Button>
        );
      })}
    </Stack>
  );
};

export const renderDesktopSkeletons = (
  requestType: string,
  isDashboardRecentActivity: boolean = false,
) => {
  if (isDashboardRecentActivity) {
    return Array.from({ length: 2 }).map((_, index) => (
      <Stack
        key={`dashboard-desktop-skeleton-${index}`}
        direction="column"
        spacing={1}
        sx={{
          paddingY: 1.5,
          paddingX: 2,
          borderBottom: index === 0 ? 1 : 0,
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="circular" width={32} height={32} />
        </Stack>
        <Skeleton variant="text" width="40%" height={16} />
        <Stack spacing={1} mt={1}>
          <Skeleton
            variant="rectangular"
            width={80}
            height={20}
            sx={{ borderRadius: 4 }}
          />
          <Skeleton
            variant="rectangular"
            width={160}
            height={5}
            sx={{ borderRadius: 5 }}
          />
        </Stack>
      </Stack>
    ));
  }

  return Array.from({ length: 6 }).map((_, index) => (
    <Stack
      key={`desktop-skeleton-${index}`}
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{
        borderBottom: "1px solid",
        borderColor: "divider",
        borderRadius: 0,
        padding: "12px 16px",
        backgroundColor: "transparent",
      }}
    >
      <Stack
        flex={1.5}
        direction="row"
        alignItems="center"
        spacing={2}
        minWidth={0}
      >
        <Skeleton
          variant="rectangular"
          width={44}
          height={44}
          sx={{ borderRadius: 2, flexShrink: 0 }}
        />
        <Box textAlign="start" minWidth={0} width="100%">
          <Skeleton
            variant="text"
            width="80%"
            height={24}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="text"
            width="50%"
            height={16}
            sx={{ mt: 0.5, borderRadius: 1 }}
          />
        </Box>
      </Stack>

      <Stack
        direction="column"
        flex={0.75}
        alignItems="center"
        justifyContent="center"
        spacing={0.5}
      >
        <Skeleton
          variant="rectangular"
          width={80}
          height={10}
          sx={{ borderRadius: 1 }}
        />
        {requestType === strings.sentRequest ? (
          <Stack direction="row" spacing={-1}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton
                key={i}
                variant="circular"
                width={32}
                height={32}
                sx={{
                  border: "2px solid white",
                  zIndex: 5 - i,
                }}
              />
            ))}
          </Stack>
        ) : (
          <></>
        )}
      </Stack>

      <Stack
        direction="row"
        spacing={6}
        alignItems="center"
        flex={0.5}
        justifyContent="flex-end"
      >
        <Stack>
          <Stack direction={"row"} spacing={0.5} alignItems={"center"}>
            <Skeleton variant="circular" width={9} height={9} />
            <Skeleton variant="text" width={90} height={15} />
          </Stack>
          <Skeleton variant="rectangular" width={130} height={4} />
        </Stack>

        <Skeleton variant="circular" width={24} height={24} />
      </Stack>
    </Stack>
  ));
};

export const renderMobileSkeletons = (
  requestType: string,
  isDashboardRecentActivity: boolean = false,
) => {
  if (isDashboardRecentActivity) {
    return Array.from({ length: 2 }).map((_, index) => (
      <Box
        key={`dashboard-mobile-skeleton-${index}`}
        sx={{
          border: "1px solid #e2e8f0",
          borderRadius: 2,
          padding: 1.5,
          backgroundColor: "#fff",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Stack spacing={1.1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Stack spacing={0.5} sx={{ width: "70%" }}>
              <Skeleton
                variant="text"
                width="95%"
                height={20}
                sx={{ borderRadius: 1 }}
              />
              <Skeleton
                variant="text"
                width="72%"
                height={20}
                sx={{ borderRadius: 1 }}
              />
            </Stack>
            <Stack direction="row" spacing={-1}>
              {[1, 2, 3].map((i) => (
                <Skeleton
                  key={i}
                  variant="circular"
                  width={24}
                  height={24}
                  sx={{ border: "2px solid white", zIndex: 4 - i }}
                />
              ))}
            </Stack>
          </Stack>
          <Skeleton
            variant="text"
            width="62%"
            height={16}
            sx={{ borderRadius: 1 }}
          />
          <Stack spacing={0.5} sx={{ width: 120 }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Skeleton variant="circular" width={9} height={9} />
              <Skeleton variant="text" width={75} height={14} />
            </Stack>
            <Skeleton
              variant="rectangular"
              width={120}
              height={5}
              sx={{ borderRadius: 5 }}
            />
          </Stack>
        </Stack>
      </Box>
    ));
  }

  return Array.from({ length: 3 }).map((_, index) => (
    <Box
      key={`mobile-skeleton-${index}`}
      sx={{
        border: "1px solid #e2e8f0",
        borderRadius: 2,
        padding: 3,
        mb: 2,
        backgroundColor: "#fff",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Stack direction="column" spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction={"column"}>
            <Skeleton
              variant="text"
              width={200}
              height={24}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              variant="text"
              width={150}
              height={24}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              variant="text"
              width={190}
              height={24}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              variant="text"
              width={90}
              height={24}
              sx={{ borderRadius: 1 }}
            />
          </Stack>
        </Stack>

        <Stack direction="row" justifyContent="start" spacing={2}>
          <Stack direction="column" spacing={1} flex={0.5}>
            <Skeleton variant="text" width={100} height={18} />
            <Skeleton variant="text" width={50} height={18} />
          </Stack>
          <Stack direction="column" spacing={1} flex={0.5}>
            <Skeleton variant="text" width={100} height={18} />
            <Skeleton variant="text" width={50} height={18} />
          </Stack>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          {requestType === strings.sentRequest ? (
            <Stack direction="row" spacing={-1}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton
                  key={i}
                  variant="circular"
                  width={32}
                  height={32}
                  sx={{
                    border: "2px solid white",
                    zIndex: 5 - i,
                  }}
                />
              ))}
            </Stack>
          ) : (
            <Skeleton variant="text" width={90} height={15} />
          )}

          <Stack>
            <Stack direction={"row"} spacing={0.5} alignItems={"center"}>
              <Skeleton variant="circular" width={9} height={9} />
              <Skeleton variant="text" width={90} height={15} />
            </Stack>
            <Skeleton variant="rectangular" width={130} height={4} />
          </Stack>
        </Stack>
      </Stack>
    </Box>
  ));
};

export const autoEmailReminderValidation = (
  errors: any,
  setErrors: Function,
  configurationFields: any,
) => {
  let isValid = true;
  let error = { ...errors };

  if (configurationFields.daysBeforeFirstReminder <= 0) {
    error.daysBeforeFirstReminder = "Please enter at least 1 day or more.";
    isValid = false;
  }
  if (configurationFields.daysBetweenReminders <= 0) {
    error.daysBetweenReminders = "Please enter at least 1 day or more.";
    isValid = false;
  }
  setErrors(error);
  return isValid;
};

export const autoExpValidation = (
  autoExpErrors: any,
  setAutoExpErrors: Function,
  configurationFields: any,
) => {
  let isValid = true;
  let error = { ...autoExpErrors };
  if (
    configurationFields.expirationDays > 180 ||
    configurationFields.expirationDays <= 0
  ) {
    error.expirationDays = "Please enter a value between 1 and 180 days.";
    isValid = false;
  }
  setAutoExpErrors(error);
  return isValid;
};

export const autoDelegationValidation = (
  autoDelegateErrors: any,
  setAutoDelegateErrors: Function,
  configurationFields: any,
) => {
  let isValid = true;
  const emailRegex = strings.regex;
  let error = { ...autoDelegateErrors };
  const delegateEmail = (configurationFields.delegateEmail || "").trim();
  if (!isTruthy(configurationFields.delegateName)) {
    error.delegationName = "Please enter name.";
    isValid = false;
  } else {
    error.delegationName = "";
  }

  if (!isTruthy(delegateEmail)) {
    error.delegationEmail = "Please enter a email.";
    isValid = false;
  } else if (delegateEmail.includes(",")) {
    error.delegationEmail = "Please enter a single valid email";
    isValid = false;
  } else if (!emailRegex.test(delegateEmail)) {
    error.delegationEmail = "Please enter a valid email";
    isValid = false;
  } else {
    error.delegationEmail = "";
  }

  if (
    !isTruthy(configurationFields.delegateStartDate) ||
    !isTruthy(configurationFields.delegateEndDate)
  ) {
    error.delegationDateRange = "Please enter start date and end date";
    isValid = false;
  } else {
    error.delegationDateRange = "";
  }
  setAutoDelegateErrors(error);
  return isValid;
};

export const handleEnableConfigUpdateBtn = (
  matchFields: string,
  value: string | boolean,
  setUpdateButtonEnabled: Function,
  updateButtonEnabled: any,
  previousValues: any,
) => {
  switch (matchFields) {
    case "autoReminder":
      setUpdateButtonEnabled({
        ...updateButtonEnabled,
        btn1: value !== previousValues[matchFields],
      });
      break;
    case "daysBeforeFirstReminder":
      setUpdateButtonEnabled({
        ...updateButtonEnabled,
        btn1: value !== previousValues[matchFields].toString(),
      });
      break;
    case "daysBetweenReminders":
      setUpdateButtonEnabled({
        ...updateButtonEnabled,
        btn1: value !== previousValues[matchFields].toString(),
      });
      break;
    case "expirationDays":
      setUpdateButtonEnabled({
        ...updateButtonEnabled,
        btn2: value !== previousValues[matchFields].toString(),
      });
      break;
    case "expirationPassword":
      setUpdateButtonEnabled({
        ...updateButtonEnabled,
        btn3: value !== previousValues[matchFields].toString(),
      });
      break;
    case "pwdExpiryEnable":
      setUpdateButtonEnabled({
        ...updateButtonEnabled,
        btn3: value !== previousValues[matchFields],
      });
      break;
    case "delegateName":
      setUpdateButtonEnabled({
        ...updateButtonEnabled,
        btn3: value !== previousValues[matchFields],
      });
      break;
    case "delegateEmail":
      setUpdateButtonEnabled({
        ...updateButtonEnabled,
        btn3: value !== previousValues[matchFields],
      });
      break;
    case "delegateStartDate":
      setUpdateButtonEnabled({
        ...updateButtonEnabled,
        btn3: value !== previousValues[matchFields],
      });
      break;
    case "delegateEndDate":
      setUpdateButtonEnabled({
        ...updateButtonEnabled,
        btn3: value !== previousValues[matchFields],
      });
      break;
    case "allowSignatureDelegation":
      setUpdateButtonEnabled({
        ...updateButtonEnabled,
        btn3: value !== previousValues[matchFields],
      });
      break;
    default:
      break;
  }
};

export const getConfigurationData = async (
  setLoading: Function,
  setConfigurationFields: Function,
  setPreviousValues: Function,
  email: string,
) => {
  setLoading(true);
  try {
    // Run config APIs independently so one failing endpoint (e.g. delegation)
    // doesn't blank out the others (e.g. reminders).
    const [
      autoRemindResult,
      expirationDaysResult,
      reminderConfigResult,
      delegationConfigResult,
    ] = await Promise.allSettled([
      getAutoEmailReminder(),
      getExpirationDays(),
      getAutoReminderConfig(),
      getAutoDelegateConfig(email),
    ]);

    const reminderConfigRes =
      reminderConfigResult.status === "fulfilled"
        ? reminderConfigResult.value || {}
        : {};
    const delegationConfigRes =
      delegationConfigResult.status === "fulfilled"
        ? delegationConfigResult.value || {}
        : {};

    const dateOnly = (v: any) =>
      typeof v === "string" && v.includes("T") ? v.split("T")[0] : v || "";

    const nextFields = {
      autoReminder:
        autoRemindResult.status === "fulfilled"
          ? (autoRemindResult.value ?? false)
          : false,
      daysBeforeFirstReminder: reminderConfigRes.daysBeforeFirstReminder ?? 0,
      daysBetweenReminders: reminderConfigRes.daysBetweenReminders ?? 0,
      expirationDays:
        expirationDaysResult.status === "fulfilled"
          ? (expirationDaysResult.value ?? 0)
          : 0,
      allowSignatureDelegation: delegationConfigRes.enabled ?? false,
      delegateName: delegationConfigRes.delegateName ?? "",
      delegateEmail: delegationConfigRes.delegateEmail ?? "",
      delegateStartDate: dateOnly(delegationConfigRes.validFrom),
      delegateEndDate: dateOnly(delegationConfigRes.validTo),
    };

    setConfigurationFields(nextFields);
    setPreviousValues(nextFields);

    // Surface the first failure (if any) without blocking the successful sections.
    const firstFailure = [
      autoRemindResult,
      expirationDaysResult,
      reminderConfigResult,
      delegationConfigResult,
    ].find((r) => r.status === "rejected") as PromiseRejectedResult | undefined;
    if (firstFailure) handleErrorMessage(firstFailure.reason);
  } catch (error: any) {
    handleErrorMessage(error);
  } finally {
    setLoading(false);
  }
};

export const handleUpdate = async (
  btnName: string,
  setLoading: Function,
  setPreviousValues: Function,
  errors: any,
  setErrors: Function,
  autoExpErrors: any,
  setAutoExpErrors: Function,
  configurationFields: any,
  setUpdateButtonEnabled: Function,
  updateButtonEnabled: any,
  autoDelegateErrors: any,
  setAutoDelegateErrors: Function,
  email: string,
) => {
  setLoading(true);
  try {
    switch (btnName) {
      case "autoEmailReminder":
        if (
          autoEmailReminderValidation(errors, setErrors, configurationFields)
        ) {
          await updateAutoEmailReminder(configurationFields.autoReminder);
          if (configurationFields.autoReminder) {
            await updateAutoReminderConfig(
              configurationFields.daysBeforeFirstReminder,
              configurationFields.daysBetweenReminders,
            );
          }
          openSuccessNotification("Automated email reminder has been updated");
          setUpdateButtonEnabled({
            ...updateButtonEnabled,
            btn1: false,
          });
        }
        break;

      case "autoExpirations":
        if (
          autoExpValidation(
            autoExpErrors,
            setAutoExpErrors,
            configurationFields,
          )
        ) {
          await updateExpirationDays(configurationFields.expirationDays);
          openSuccessNotification("Auto expirations has been updated");
          setUpdateButtonEnabled({
            ...updateButtonEnabled,
            btn2: false,
          });
        }
        break;
      case "autoDelegation":
        if (
          autoDelegationValidation(
            autoDelegateErrors,
            setAutoDelegateErrors,
            configurationFields,
          )
        ) {
          const body = {
            enabled: configurationFields.allowSignatureDelegation,
            delegateName: configurationFields.delegateName,
            delegateEmail: configurationFields.delegateEmail,
            validFrom: formatDateToStartOfDay(
              configurationFields.delegateStartDate,
            ),
            validTo: formatDateToEndOfDay(configurationFields.delegateEndDate),
          };

          await updateDelegation(body, email);
          openSuccessNotification("Automated delegation has been updated");
          setUpdateButtonEnabled({
            ...updateButtonEnabled,
            btn3: false,
          });
        }
        break;
      default:
        break;
    }
    setPreviousValues(configurationFields);
  } catch (error: any) {
    handleErrorMessage(error);
  } finally {
    setLoading(false);
  }
};

export const configurationSkeleton = () => {
  const cardSx = {
    border: "none",
    boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.1)",
    width: "100%",
    display: "flex",
    flexDirection: "column",
  };
  const itemSx = { display: "flex" };
  return (
    <Box
      sx={{
        pt: "10px",
        width: "100%",
        display: "grid",
        gap: 2,
        gridTemplateColumns: {
          xs: "minmax(0,1fr)",
          md: "repeat(2,minmax(0,1fr))",
        },
        alignItems: "stretch",
      }}
    >
      {/* Automated Email Reminder */}
      <Box sx={itemSx}>
        <Card variant="outlined" sx={cardSx}>
          <CardContent sx={{ p: 3 }}>
            <Grid container>
              <Grid item xs={12} display="flex" alignItems="center">
                <Skeleton
                  variant="text"
                  width={250}
                  height={32}
                  sx={{ mr: 2 }}
                />
                <Skeleton
                  variant="rectangular"
                  width={44}
                  height={24}
                  sx={{ borderRadius: 12 }}
                />
              </Grid>
              <Grid item xs={12} mt={1}>
                <Skeleton variant="text" width="80%" height={20} />
                <Skeleton variant="text" width="60%" height={20} />
              </Grid>
              <Grid item xs={12} mt={2}>
                <Grid container direction="row" spacing={2} alignItems="center">
                  <Grid item>
                    <Skeleton variant="text" width={120} height={24} />
                  </Grid>
                  <Grid item>
                    <Skeleton
                      variant="rectangular"
                      width={60}
                      height={40}
                      sx={{ borderRadius: 1 }}
                    />
                  </Grid>
                  <Grid item>
                    <Skeleton variant="text" width={40} height={24} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} mt={1}>
                <Grid container direction="row" spacing={2} alignItems="center">
                  <Grid item>
                    <Skeleton variant="text" width={140} height={24} />
                  </Grid>
                  <Grid item>
                    <Skeleton
                      variant="rectangular"
                      width={60}
                      height={40}
                      sx={{ borderRadius: 1 }}
                    />
                  </Grid>
                  <Grid item>
                    <Skeleton variant="text" width={40} height={24} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} mt={2}>
                <Skeleton
                  variant="rectangular"
                  width={80}
                  height={32}
                  sx={{ borderRadius: 1 }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Auto Expiration */}
      <Box sx={itemSx}>
        <Card variant="outlined" sx={cardSx}>
          <CardContent sx={{ p: 3 }}>
            <Grid container>
              <Grid item xs={12} display="flex" alignItems="center">
                <Skeleton variant="text" width={150} height={32} />
              </Grid>
              <Grid item xs={12} mt={2}>
                <Skeleton variant="text" width="90%" height={20} />
                <Skeleton variant="text" width="70%" height={20} />
              </Grid>
              <Grid item xs={12} mt={2}>
                <Grid container direction="row" spacing={2} alignItems="center">
                  <Grid item>
                    <Skeleton variant="text" width={150} height={24} />
                  </Grid>
                  <Grid item>
                    <Skeleton
                      variant="rectangular"
                      width={60}
                      height={40}
                      sx={{ borderRadius: 1 }}
                    />
                  </Grid>
                  <Grid item>
                    <Skeleton variant="text" width={40} height={24} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} mt={2}>
                <Skeleton
                  variant="rectangular"
                  width={80}
                  height={32}
                  sx={{ borderRadius: 1 }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Automated Delegation */}
      <Box sx={itemSx}>
        <Card variant="outlined" sx={cardSx}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} display="flex" alignItems="center">
                <Skeleton
                  variant="text"
                  width={220}
                  height={32}
                  sx={{ mr: 2 }}
                />
                <Skeleton
                  variant="rectangular"
                  width={44}
                  height={24}
                  sx={{ borderRadius: 12 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Skeleton variant="text" width="90%" height={18} />
              </Grid>
              <Grid item xs={12} display="flex" gap={2}>
                <Skeleton
                  variant="rectangular"
                  width="50%"
                  height={40}
                  sx={{ borderRadius: 1 }}
                />
                <Skeleton
                  variant="rectangular"
                  width="50%"
                  height={40}
                  sx={{ borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Skeleton
                  variant="text"
                  width={160}
                  height={20}
                  sx={{ mb: 0.5 }}
                />
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={40}
                  sx={{ borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Skeleton
                  variant="rectangular"
                  width={100}
                  height={36}
                  sx={{ borderRadius: 1 }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Default Date Format */}
      <Box sx={itemSx}>
        <Card variant="outlined" sx={cardSx}>
          <CardContent sx={{ p: 3 }}>
            <Grid container>
              <Grid item xs={12} display="flex" alignItems="center">
                <Skeleton variant="text" width={200} height={32} />
              </Grid>
              <Grid item xs={12} mt={1}>
                <Skeleton variant="text" width="90%" height={20} />
                <Skeleton variant="text" width="70%" height={20} />
              </Grid>
              <Grid item xs={12} mt={1}>
                <Skeleton
                  variant="text"
                  width={100}
                  height={20}
                  sx={{ mb: 0.5 }}
                />
                <Skeleton
                  variant="rectangular"
                  width={200}
                  height={40}
                  sx={{ borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={12} mt={2}>
                <Skeleton
                  variant="rectangular"
                  width={80}
                  height={32}
                  sx={{ borderRadius: 1 }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export const downloadSignedFileAsZip = async (
  signRequest: SignRequest,
  setLoading: Function,
  envelopeId: string,
  requestConfig: SignRequestConfig,
) => {
  const generateSingedPdf = {
    files: signRequest.files,
    id: envelopeId,
    signRequestConfig: requestConfig,
  };
  try {
    setLoading(true);
    const res = await downloadSignedPdfAsZip(generateSingedPdf);
    const url = URL.createObjectURL(res);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${signRequest.emailSubject}.zip`;
    link.click();
    URL.revokeObjectURL(link.href);
    setLoading(false);
  } catch (error: any) {
    setLoading(false);
    handleErrorMessage(error);
  }
};

/**
 * Same bundle endpoint as {@link downloadSignedFileAsZip}, narrowed to a single
 * document so the Sign Request Detail document rows can download one file.
 */
export const downloadSignedDocument = async (
  signRequest: SignRequest,
  file: RequestFile,
  setLoading: Function,
) => {
  const fileName =
    file.unsignedPdf || file.unsignedDoc || signRequest.emailSubject;
  try {
    setLoading(true);
    const res = await downloadSignedPdfAsZip({
      files: [file],
      id: signRequest.id,
      signRequestConfig: signRequest.signRequestConfig,
    });
    const url = URL.createObjectURL(res);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName}.zip`;
    link.click();
    URL.revokeObjectURL(link.href);
    setLoading(false);
  } catch (error: any) {
    setLoading(false);
    handleErrorMessage(error);
  }
};

export const voidSignRequestHandle = async (
  reqId: string,
  setLoading: Function,
  setVoidPopup: Function,
  setAnchorEl: Function,
) => {
  try {
    setVoidPopup(false);
    setLoading(true);
    const res = await voidSignRequest(reqId);
    openSuccessNotification(res.message);
    setAnchorEl(null);
  } catch (error: any) {
    handleErrorMessage(error);
    setAnchorEl(null);
  } finally {
    setLoading(false);
    setAnchorEl(null);
  }
};

export const resendSignRequestHandle = async (
  reqId: string,
  setLoading: Function,
  setResendPopup: Function,
  setAnchorEl: Function,
  email?: string,
) => {
  try {
    setResendPopup(false);
    setLoading(true);
    const res = email
      ? await resendSignRequest(reqId, email)
      : await resendSignRequest(reqId);
    openSuccessNotification(res.message);
    setAnchorEl(null);
  } catch (error: any) {
    setAnchorEl(null);
    handleErrorMessage(error);
  } finally {
    setAnchorEl(null);
    setLoading(false);
  }
};

export const getColor = (name: string) => {
  const letter = name.trim()[0]?.toUpperCase();
  return getAvatarColors(letter) || { bg: "#e0e0e0", color: "#424242" };
};

const COPY_KEEP_VALUE_TYPES = ["text", "checkbox", "radio_button", "date"];

/**
 * Whether a request loaded from the server (to copy, use as a template, edit or resume) should
 * start with auto reminders on. The source's own `needReminders` wins: a request sent with
 * reminders OFF still carries a `reminders` object (the day counts), so inferring "on" from that
 * object turned reminders back on in every copy. The object is only a fallback for a source that
 * doesn't say either way.
 */
export const resolveNeedReminders = (source: Partial<SignRequest>): boolean =>
  typeof source.needReminders === "boolean"
    ? source.needReminders
    : source.reminders != null;

export const copySignRequest = (
  redirect: boolean,
  signRequest: SignRequest,
  dispatch: any,
  envelopeId?: string,
  copyWithValues?: boolean,
) => {
  try {
    const copiedRequest = {
      ...structuredClone(signRequest),
      requestedBy: null,
      status: "",
    };

    if (envelopeId) copiedRequest.id = envelopeId;
    copiedRequest.files.forEach((file: RequestFile) => {
      file.fields.forEach((field: FileField) => {
        const typeNorm = (field.type || "").toLowerCase().replace(/\s/g, "_");
        const keepValue =
          copyWithValues === true &&
          COPY_KEEP_VALUE_TYPES.some(
            (t) => typeNorm === t || typeNorm?.includes(t),
          );
        if (!keepValue) {
          if (typeNorm === "email") {
            // Email fields are cleared like every other value, but instead of
            // leaving them blank we re-seed them with the email of the signer
            // the field belongs to (fields link to signers via
            // field.signerIndex === signer.id).
            const owner = copiedRequest.signers?.find(
              (s: RequestParticipant) => s.id === field.signerIndex,
            );
            field.value = owner?.email ?? "";
          } else {
            field.value = "";
          }
        }
        // A copy starts with its any-signer fields unclaimed, whatever the
        // original's signers filled.
        if (field.filledBy) field.filledBy = "";
      });
      // Mark as uploading so DraggableChip shows progress immediately
      (file as any).isUploading = true;
    });
    // Per-envelope verification state (current OTP, verification result,
    // attempt/lock counters) should not survive a copy — only the
    // configuration the requester chose (security mode, etc.). Returns a
    // fresh requestSecurity with code/OTP/attempt fields cleared but the
    // needSecuredAccess / needOTPValidation flags preserved so the dropdown
    // selection persists in the copy form.
    const resetSecurityForCopy = (
      sec: RequestParticipant["requestSecurity"],
    ): RequestParticipant["requestSecurity"] =>
      sec
        ? {
            ...sec,
            securityCode: null,
            otp: null,
            otpValidUntil: null,
            isVerified: false,
            verified: false,
            verifiedAt: null,
            failedAttempts: 0,
            // Server-redacted (e.g. "••••4567"), same as the other
            // per-envelope verification state above — stale once copied.
            authPhoneHint: null,
          }
        : sec;

    if (copiedRequest.signers) {
      copiedRequest.signers = (copiedRequest.signers || [])
        .filter(
          (signer: RequestParticipant) => signer.status !== strings.delegated,
        )
        .map((signer: RequestParticipant) => ({
          ...signer,
          delegated: false,
          requestSecurity: resetSecurityForCopy(signer.requestSecurity),
        }));
    }
    if (copiedRequest.approval?.reviewers) {
      copiedRequest.approval.reviewers = copiedRequest.approval.reviewers.map(
        (reviewer: RequestParticipant) => ({
          ...reviewer,
          requestSecurity: resetSecurityForCopy(reviewer.requestSecurity),
        }),
      );
    }
    copiedRequest.needReminders = resolveNeedReminders(copiedRequest);

    if (redirect) {
      history.push({
        pathname: viewpaths.copySignRequestViewPath,
        state: copiedRequest,
      });
    }
    dispatch(setLoading(false));
    dispatch(setChipLoader(true));
    return copiedRequest;
  } catch (error) {
    console.log(error);
  }
};
